import { useState } from 'react';
import apiClient from '../../../services/api/client';
import { BlobFile, FileOption } from '../types';

interface UseFilesLoaderProps {
  clientId?: string;
  workflowId?: string;
  workflowPath?: string;
  clientCode?: string;
  externalId?: string;
  onClassificationFilesFound?: (files: {[key: string]: string}) => void;
}

export const useFilesLoader = ({
  clientId,
  workflowId,
  workflowPath,
  clientCode,
  externalId,
  onClassificationFilesFound
}: UseFilesLoaderProps) => {
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load available files from storage
  const loadAvailableFiles = async () => {
    if (!clientId || !workflowId) {
      setError('Missing client ID or workflow ID');
      return { stemWordsFiles: [], keywordsFiles: [] };
    }
    
    setIsLoadingFiles(true);
    setError(null);
    
    // Create a derived workflow path if needed
    let effectiveWorkflowPath = workflowPath;
    
    // Construct path if missing but we have clientCode and externalId
    if (!effectiveWorkflowPath && clientCode && externalId) {
      effectiveWorkflowPath = `client-data/${clientCode}/workflows/${externalId}/outputs/module1`;
    }
    
    try {
      const stemWordsFiles: FileOption[] = [];
      const keywordsFiles: FileOption[] = [];
      
      // Check various possible paths to find stem words files
      await tryFindStemWordFiles(effectiveWorkflowPath, stemWordsFiles);
      
      // If still no stem word files, try with more methods
      if (stemWordsFiles.length === 0) {
        await tryAlternativePaths(clientCode, externalId, stemWordsFiles);
        
        // If still no files, try standard listing
        if (stemWordsFiles.length === 0) {
          await tryStandardListing(clientId, workflowId, stemWordsFiles, keywordsFiles);
        }
      }
      
      // If still no keyword files, try listing specifically
      if (keywordsFiles.length === 0) {
        await tryFindKeywordFiles(clientId, workflowId, keywordsFiles);
      }
      
      // Filter for "final" files if available
      const finalFiles = stemWordsFiles.filter((f: FileOption) => 
        f.label.toLowerCase().includes('final')
      );
      
      const filesToShow = finalFiles.length > 0 ? finalFiles : stemWordsFiles;
      
      console.log(`Found ${stemWordsFiles.length} stem word files and ${keywordsFiles.length} keyword files`);
      
      // Set error messages if needed
      if (stemWordsFiles.length === 0) {
        setError('No stem word files found. Please make sure Module 1 has been run successfully.');
      }
      
      if (keywordsFiles.length === 0) {
        setError((prev) => 
          prev ? `${prev} No keyword files found.` : 'No keyword files found in the input directory.'
        );
      }
      
      setIsLoadingFiles(false);
      return { stemWordsFiles: filesToShow, keywordsFiles };
    } catch (err) {
      console.error('Error loading available files:', err);
      setError('Failed to load available files. Please try again.');
      setIsLoadingFiles(false);
      return { stemWordsFiles: [], keywordsFiles: [] };
    }
  };

  // Helper function to try finding stem word files in a specific path
  const tryFindStemWordFiles = async (path?: string, results: FileOption[] = []) => {
    if (!path) return;
    
    try {
      const response = await apiClient.get('/api/v1/storage/list', {
        params: {
          container: 'client-data',
          path: path,
          add_client_prefix: false
        }
      });
      
      if (response.data?.files) {
        response.data.files.forEach((file: BlobFile) => {
          const filePath = file.path || '';
          const fileName = file.name;
          
          if (fileName.endsWith('.json')) {
            results.push({
              label: fileName,
              value: fileName,
              path: filePath
            });
          }
        });
      }
    } catch (err) {
      console.error(`Error listing files from path ${path}:`, err);
    }
  };

  // Try alternative paths for stem word files
  const tryAlternativePaths = async (clientCode?: string, externalId?: string, results: FileOption[] = []) => {
    if (!clientCode || !externalId) return;
    
    const alternativePaths = [
      `client-data/${clientCode}/workflows/${externalId}/module1/output`,
      `client-data/${clientCode}/workflows/${externalId}/output/module1`,
      `client-data/${clientCode}/module1/${externalId}/output`,
      `${clientCode}/workflows/${externalId}/outputs/module1`,
    ];
    
    for (const path of alternativePaths) {
      if (results.length > 0) break;
      await tryFindStemWordFiles(path, results);
    }
  };

  // Try standard listing for both stem words and keywords
  const tryStandardListing = async (clientId: string, workflowId: string, stemWordsResults: FileOption[] = [], keywordsResults: FileOption[] = []) => {
    try {
      const response = await apiClient.get('/api/v1/storage/list', {
        params: {
          container: 'client-data',
          client_id: clientId,
          workflow_id: workflowId
        }
      });
      
      if (response.data?.files) {
        const classificationFiles: {[key: string]: string} = {};
        
        response.data.files.forEach((file: BlobFile) => {
          const filePath = file.path || '';
          const fileName = file.name;
          
          // Check for stem word files
          if ((filePath.includes('/module1/output/') || filePath.includes('/outputs/module1/')) &&
              (fileName.endsWith('.json'))) {
            stemWordsResults.push({
              label: fileName,
              value: fileName,
              path: filePath
            });
          }
          
          // Check for keyword files
          else if ((filePath.includes('/input/') || filePath.includes('/uploads/')) && 
                  fileName.endsWith('.csv')) {
            const isClassificationFile = 
              fileName === 'relevant.csv' || 
              fileName === 'outlier.csv' || 
              fileName === 'undecided.csv' || 
              fileName === 'stem_words_hierarchy.csv';
            
            if (!isClassificationFile) {
              keywordsResults.push({
                label: fileName,
                value: fileName,
                path: filePath
              });
            } else {
              // Classification file found
              classificationFiles[fileName] = filePath;
            }
          }
        });
        
        if (Object.keys(classificationFiles).length > 0 && onClassificationFilesFound) {
          onClassificationFilesFound(classificationFiles);
        }
      }
    } catch (err) {
      console.error('Error with standard file listing:', err);
    }
  };

  // Try finding keyword files specifically
  const tryFindKeywordFiles = async (clientId: string, workflowId: string, results: FileOption[] = []) => {
    try {
      const response = await apiClient.get('/api/v1/storage/list', {
        params: {
          container: 'client-data',
          client_id: clientId,
          workflow_id: workflowId,
          directory: 'input'
        }
      });
      
      if (response.data?.files) {
        const classificationFiles: {[key: string]: string} = {};
        
        response.data.files.forEach((file: BlobFile) => {
          const filePath = file.path || '';
          const fileName = file.name.toLowerCase();
          
          if (fileName.endsWith('.csv')) {
            const isClassificationFile = 
              fileName === 'relevant.csv' || 
              fileName === 'outlier.csv' || 
              fileName === 'undecided.csv' || 
              fileName === 'stem_words_hierarchy.csv' || 
              fileName === 'topic_framework.json';
            
            if (!isClassificationFile) {
              results.push({
                label: file.name,
                value: file.name,
                path: filePath
              });
            } else {
              // Classification file found
              classificationFiles[fileName] = filePath;
            }
          }
        });
        
        if (Object.keys(classificationFiles).length > 0 && onClassificationFilesFound) {
          onClassificationFilesFound(classificationFiles);
        }
      }
    } catch (err) {
      console.error('Error listing keyword files:', err);
    }
  };

  return {
    isLoadingFiles,
    error,
    loadAvailableFiles,
    setError
  };
};
